import React, { createContext, useContext, useState, useEffect } from "react";

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load cart from localStorage on mount
  useEffect(() => {
    try {
      const localCart = localStorage.getItem("cart");
      if (localCart) {
        setCart(JSON.parse(localCart));
      }
    } catch (error) {
      console.error("Error loading cart from localStorage:", error);
    }
    setLoading(false);
  }, []);

  // Save to localStorage whenever cart changes
  useEffect(() => {
    if (!loading) {
      localStorage.setItem("cart", JSON.stringify(cart));
    }
  }, [cart, loading]);

  const addToCart = (product, variant, quantity = 1) => {
    try {
      const existingItemIndex = cart.findIndex(
        (item) => item.id === product.id && item.variant.size === variant.size
      );

      if (existingItemIndex > -1) {
        const newCart = [...cart];
        newCart[existingItemIndex].quantity += quantity;
        setCart(newCart);
      } else {
        const newCart = [
          ...cart,
          {
            id: product.id,
            name: product.name,
            image: product.image,
            variant: variant,
            quantity: quantity,
          },
        ];
        setCart(newCart);
      }
      return true;
    } catch (error) {
      console.error("Error adding to cart:", error);
      return false;
    }
  };

  const removeFromCart = (productId, variantSize) => {
    try {
      const newCart = cart.filter(
        (item) => !(item.id === productId && item.variant.size === variantSize)
      );
      setCart(newCart);
      return true;
    } catch (error) {
      console.error("Error removing from cart:", error);
      return false;
    }
  };

  const updateQuantity = (productId, variantSize, quantity) => {
    try {
      if (quantity <= 0) {
        removeFromCart(productId, variantSize);
        return true;
      }

      const newCart = cart.map((item) =>
        item.id === productId && item.variant.size === variantSize
          ? { ...item, quantity }
          : item
      );
      setCart(newCart);
      return true;
    } catch (error) {
      console.error("Error updating quantity:", error);
      return false;
    }
  };

  const clearCart = () => {
    try {
      setCart([]);
      localStorage.removeItem("cart");
      return true;
    } catch (error) {
      console.error("Error clearing cart:", error);
      return false;
    }
  };

  const getCartTotal = () => {
    return cart.reduce(
      (total, item) => total + item.variant.offerPrice * item.quantity,
      0
    );
  };

  const getCartCount = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  const value = {
    cart,
    loading,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartCount,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
